import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Button,
  Grid,
  Link,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { Box, Container } from '@mui/system';

import DynamicBreadcrumbs from '@/components/nav/Breadcrumb';
import ResourcesRightSidebar from '@/components/nav/ResourcesRightSidebar';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';

const Form1098CInstructions = () => {
  return (
    <AppLayout
      meta={
        <Meta
          title="IRS Form 1098-C instructions for Tax Year 2025"
          description="Get clear instructions for Form 1098-C for 2025. Learn the filing requirements, reporting rules, and follow step-by-step guidance to report charitable contributions accurately."
          keywords=""
          canonical="resources/information-returns/form-1098-c-instructions/"
          ogType="article"
          jsonLdPricing="2.49"
          jsonLdAggregateRatingValue="5.00"
          jsonLdAggregateRatingCount="57448"
        />
      }
    >
      <Container maxWidth="lg" sx={{ textAlign: 'center' }}>
        <Typography
          variant="h1"
          sx={{
            fontSize: { lg: 40, md: 36, sm: 32, xs: 24 },
            fontWeight: 600,

            mt: 3,
            textAlign: 'center',
          }}
        >
          <span className="highlight-container">
            <span className="highlight">Form 1098-C</span>
          </span>{' '}
          Instructions
        </Typography>
        <Typography
          variant="body1"
          sx={{
            fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
            fontWeight: 400,
            mt: 1,
            mb: 2,
          }}
        >
          E-file your IRS Form 1098-C for the 2025 tax year with TaxZerone!
        </Typography>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Button
            variant="contained"
            className="btn-primary medium"
            sx={{
              fontSize: {
                xs: '16px !important',
                sm: '18px !important',
                lg: '24px !important',
              },
              transition: 'all 0.3s ease 0s',
              '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
            }}
            href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=1098cinstructions`}
          >
            E-File Now
          </Button>
        </Box>
      </Container>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          py: 5,
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        mt={0}
      >
        <Container maxWidth="lg" sx={{ mb: 0 }}>
          <DynamicBreadcrumbs />
          <Grid
            container
            spacing={0}
            rowSpacing={0}
            m={0}
            sx={{ flexDirection: 'row-reverse' }}
          >
            <Grid
              size={{ xs: 12, md: 3.5, sm: 4.5 }}
              sx={{ display: { xs: 'none', sm: 'block' } }}
            >
              <ResourcesRightSidebar />
            </Grid>
            <Grid size={{ xs: 12, md: 8.5, sm: 7.5 }}>
              <Paper
                variant="outlined"
                sx={{
                  border: '1px solid #ccc',
                  p: 3,
                  px: 3,
                  textAlign: 'left',
                  mb: 0,
                }}
              >
                <Box
                  itemScope
                  itemType="https://schema.org/Article"
                  sx={{
                    p: 3,
                    px: 3,
                    backgroundColor: '#f0f5f8',
                    borderLeft: '5px solid #01456c',
                    mb: 4,
                    borderRadius: 3,
                    position: 'relative',
                  }}
                >
                  <link
                    itemProp="mainEntityOfPage"
                    href="https://www.taxzerone.com/resources/information-returns/form-1098-c-instructions/"
                  />

                  {/* Article requires an image */}
                  <div
                    itemProp="image"
                    itemScope
                    itemType="https://schema.org/ImageObject"
                    style={{ display: 'none' }}
                  >
                    <link
                      itemProp="url"
                      href="https://www.taxzerone.com/assets/images/logo.webp"
                    />
                    <data itemProp="width" value="380" />
                    <data itemProp="height" value="124" />
                  </div>

                  <Box
                    sx={{
                      display: 'grid',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      textAlign: 'left',
                      mb: 2,
                    }}
                  >
                    <Typography variant="body2">
                      Last updated:{' '}
                      <time
                        itemProp="datePublished"
                        dateTime="2026-07-07T09:00:00-05:00"
                      >
                        July 7, 2026
                      </time>
                      <time
                        itemProp="dateModified"
                        dateTime="2026-07-07T09:00:00-05:00"
                        style={{ display: 'none' }}
                      >
                        July 7, 2026
                      </time>
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600 }}
                      itemProp="author"
                      itemScope
                      itemType="https://schema.org/Person"
                    >
                      -by{' '}
                      <span itemProp="name">
                        <a
                          itemProp="url"
                          href={`${process.env.NEXT_PUBLIC_HOMESITE_URL}`}
                          style={{ color: 'inherit', textDecoration: 'none' }}
                        >
                          Alexia Zepeda
                        </a>
                      </span>{' '}
                      | TaxZerone
                    </Typography>
                  </Box>

                  <Typography
                    variant="h4"
                    itemProp="headline"
                    sx={{
                      fontWeight: 600,
                      mb: 2,
                      fontSize: { md: 20, sm: 18, xs: 16 },
                      color: '#01456c',
                      fontStyle: 'italic',
                    }}
                  >
                    Form 1098-C at a Glance
                  </Typography>

                  <Box
                    component="ul"
                    itemScope
                    itemType="https://schema.org/ItemList"
                    sx={{
                      mb: 0,
                      pl: 0,
                      listStyle: 'none',

                      '& li': {
                        mb: 1.6,
                        position: 'relative',
                        pl: 3,
                      },

                      '& li::before': {
                        content: '"★"',
                        position: 'absolute',
                        left: 0,
                        top: '-2px',
                        color: '#01456c',
                        fontSize: '18px',
                      },
                    }}
                  >
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="1" />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>
                          {' '}
                          Form 1098-C
                        </strong>{' '}
                        reports qualified vehicle donations so donors can claim
                        tax deductions and charities can meet IRS reporting
                        requirements.
                      </span>
                    </li>

                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="2" />
                      <span itemProp="name">
                        Donor copies must generally be furnished within 30 days
                        of the sale, exchange, or transfer of the donated
                        vehicle, or within 30 days after the organization
                        receives the donated property if it intends to use the
                        vehicle for charitable purposes. The IRS filing deadline
                        is{' '}
                        <strong style={{ fontWeight: 600 }}>February 28</strong>{' '}
                        for paper filings and{' '}
                        <strong style={{ fontWeight: 600 }}>March 31</strong>{' '}
                        for electronic filings
                      </span>
                    </li>

                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="3" />
                      <span itemProp="name">
                        This article covers line-by-line instructions, including{' '}
                        <strong style={{ fontWeight: 600 }}>
                          {' '}
                          donor and organization details, Box 1
                        </strong>{' '}
                        (Vehicle Identification Number),{' '}
                        <strong style={{ fontWeight: 600 }}>Box 2</strong>
                        (Date of Contribution),{' '}
                        <strong style={{ fontWeight: 600 }}>Box 3</strong>
                        (Gross Proceeds from Sale),
                        <strong style={{ fontWeight: 600 }}>Box 4</strong>
                        (Vehicle Donated to a Needy Individual),{' '}
                        <strong style={{ fontWeight: 600 }}>Box 5</strong>
                        (Vehicle Used for a Significant Intervention),{' '}
                        <strong style={{ fontWeight: 600 }}>Box 6</strong>
                        (Description of Material Improvements or Significant
                        Intervening Use), and{' '}
                        <strong style={{ fontWeight: 600 }}>Box 7</strong>
                        (Fair Market Value of the Vehicle).
                      </span>
                    </li>

                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="4" />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}> Penalties:</strong>{' '}
                        Late, incorrect, or incomplete filing of Form 1098-C may
                        result in IRS penalties ranging from
                        <strong style={{ fontWeight: 600 }}>
                          {' '}
                          $60 to $680 per return
                        </strong>
                        , depending on the delay, correction requirements, and
                        whether the organization meets electronic filing
                        obligations.
                      </span>
                    </li>

                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="5" />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>
                          {' '}
                          Extensions:
                        </strong>{' '}
                        <Link
                          href="/tax-extension/efile-irs-form-8809-online/"
                          sx={{ color: '#0202ff' }}
                        >
                          File Form 8809
                        </Link>{' '}
                        to request an automatic filing extension.{' '}
                        <Link
                          href="/tax-extension/file-irs-form-15397-online/"
                          sx={{ color: '#0202ff' }}
                        >
                          File Form 15397
                        </Link>{' '}
                        to request the full{' '}
                        <strong style={{ fontWeight: 600 }}>
                          30-day extension
                        </strong>{' '}
                        for furnishing recipient copies.
                      </span>
                    </li>
                  </Box>

                  {/* Publisher needs an Organization with a logo ImageObject — no meta tags, same pattern */}
                  <div
                    itemProp="publisher"
                    itemScope
                    itemType="https://schema.org/Organization"
                    style={{ display: 'none' }}
                  >
                    <span itemProp="name">TaxZerone</span>
                    <div
                      itemProp="logo"
                      itemScope
                      itemType="https://schema.org/ImageObject"
                    >
                      <link
                        itemProp="url"
                        href="https://www.taxzerone.com/assets/images/logo.webp"
                      />
                      <data itemProp="width" value="380" />
                      <data itemProp="height" value="124" />
                    </div>
                  </div>
                </Box>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {' '}
                  Form 1098-C, Contributions of Motor Vehicles, Boats, and
                  Airplanes are used by charitable organizations to report
                  donations of qualifying vehicles to the IRS. This form is
                  essential for both donors and the IRS, as it allows donors to
                  claim tax deductions while ensuring compliance with tax laws.
                </Typography>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  {' '}
                  If the organization sells the vehicle, the deduction is
                  generally limited to the sale proceeds. Organizations must
                  provide the donor with a copy of the form within 30 days of
                  the donation or sale and submit it to the IRS by the
                  appropriate deadline.{' '}
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Who needs to file form 1098-C?
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  A donee organization that receives a qualified vehicle with a
                  claimed value exceeding{' '}
                  <span style={{ fontWeight: 600 }}> $500</span> must file a
                  separate Form 1098-C with the IRS for each contribution.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Important deadline for filing form 1098-C:
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If you&apos;re reporting a donated motor vehicle, boat, or
                  airplane, it&apos;s crucial to stay aware of the{' '}
                  <Link
                    href="/resources/information-returns/form-1098-c-due-dates/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    Form 1098-C deadline
                  </Link>{' '}
                  to avoid penalties and ensure donors receive their tax
                  deduction in time.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  For the <span style={{ fontWeight: 600 }}>2025 tax year</span>
                  , the key deadlines are:{' '}
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  To IRS:
                </Typography>
                <Box component="ol" sx={{ mb: 3.5, '& li': { mb: 1.5 } }}>
                  <li>
                    <b> E-Filing:</b> The IRS encourages charity organizations
                    to{' '}
                    <Link
                      href="/information-returns/efile-irs-form-1098-c/"
                      sx={{ color: '#0202ff' }}
                    >
                      file Form 1098-C
                    </Link>{' '}
                    using electronic filing. Electronic filing is mandatory to
                    file the form if the organization is submitting{' '}
                    <span style={{ fontWeight: 600 }}> 10 or more forms</span>{' '}
                    in information returns.
                    <br />
                    <Box className="deadline-dates" sx={{}}>
                      <Typography
                        variant="body1"
                        sx={{
                          p: 2,
                          color: '#fff',
                          fontSize: 18,
                          fontWeight: 500,
                        }}
                      >
                        <b style={{ fontSize: 22 }}>
                          {' '}
                          Deadline for the Tax year 2025 :
                        </b>{' '}
                        March 31, 2026
                      </Typography>
                    </Box>
                  </li>
                  <li>
                    <b> Paper filing:</b> Charity Organization can using the
                    paper filing method if they file less than 10 forms
                    <br />
                    <Box className="deadline-dates" sx={{}}>
                      <Typography
                        variant="body1"
                        sx={{
                          p: 2,
                          color: '#fff',
                          fontSize: 18,
                          fontWeight: 500,
                        }}
                      >
                        <b style={{ fontSize: 22 }}>
                          {' '}
                          Deadline for the Tax year 2025 :
                        </b>{' '}
                        March 2, 2026
                      </Typography>
                    </Box>
                  </li>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  To the Donor:
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  {' '}
                  The donee organization must provide a copy of Form 1098-C to
                  the donor{' '}
                  <span style={{ fontWeight: 600 }}> within 30 days</span> of
                  the donation or the sale of the vehicle.
                </Typography>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mt: 2, mb: 4, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <b>Note:</b> If the deadline falls on the federal holiday, it
                  will extend to the next business day.
                </Alert>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mt: 4,
                    mb: 1,
                  }}
                >
                  Form 1098-C instructions-how to fill out?
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Step-by-step instructions to fill out 1098-C form
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                    mb: 1,
                    fontWeight: 600,
                  }}
                >
                  Step 1: Enter the Donee’s details
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f1098-c-donee-details-img.webp" />
                  <source srcSet="/assets/images/f1098-c-donee-details-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f1098-c-donee-details-img.png"
                    loading="lazy"
                    alt="Step 1: Enter the Donee’s details"
                    width={410}
                    height={242}
                    className="img-responsive"
                    sx={{
                      border: '1px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the Donee’s name, address and telephone number along
                  with the donee’s Taxpayer Identification Number (TIN).
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 1,
                    fontWeight: 600,
                  }}
                >
                  Step 2: Enter the Donor’s details
                </Typography>{' '}
                <picture>
                  <source srcSet="/assets/images/f1098-c-donors-img.webp" />
                  <source srcSet="/assets/images/f1098-c-donors-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f1098-c-donors-img.png"
                    loading="lazy"
                    alt="Step 2: Enter the Donor’s details"
                    width={410}
                    height={222}
                    className="img-responsive"
                    sx={{
                      border: '1px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the donor&apos;s Taxpayer Identification Number (TIN),
                  full name, and complete address, including apartment number,
                  city, state, country, and ZIP code.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 1,
                    fontWeight: 600,
                  }}
                >
                  Step 3: Contributed property details
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 1: Date of Contribution
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f1098-c-contribution-img.webp" />
                  <source srcSet="/assets/images/f1098-c-contribution-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f1098-c-contribution-img.png"
                    loading="lazy"
                    alt="Box 1: Date of Contribution"
                    width={170}
                    height={62}
                    className="img-responsive"
                    sx={{
                      border: '1px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the date the motor vehicle, boat, or airplane was
                  received from the donor.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Boxes 2a- 2d: Odometer Mileage, Year, Make, and Model of
                  Vehicle
                </Typography>
                <Box>
                  <picture>
                    <source srcSet="/assets/images/f1098-c-mileage-img.webp" />
                    <source srcSet="/assets/images/f1098-c-mileage-img.png" />
                    <Box
                      component="img"
                      src="/assets/images/f1098-c-mileage-img.png"
                      loading="lazy"
                      alt="Boxes 2a- 2d: Odometer Mileage, Year, Make, and Model of Vehicle"
                      width={172}
                      height={62}
                      className="img-responsive"
                      sx={{
                        border: '1px solid #333',
                        maxWidth: '100%',
                        mb: 2,
                      }}
                    />
                  </picture>
                  <Box>
                    <picture>
                      <source srcSet="/assets/images/f1098-c-model-img.webp" />
                      <source srcSet="/assets/images/f1098-c-model-img.png" />
                      <Box
                        component="img"
                        src="/assets/images/f1098-c-model-img.png"
                        loading="lazy"
                        alt="Boxes 2a- 2d: Odometer Mileage, Year, Make, and Model of Vehicle"
                        width={338}
                        height={42}
                        className="img-responsive"
                        sx={{
                          border: '1px solid #333',
                          maxWidth: '100%',
                          mb: 1,
                        }}
                      />
                    </picture>
                  </Box>
                </Box>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the odometer mileage reading (for motor vehicles only),
                  along with the make, model, and year of the donated vehicle.
                </Typography>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mb: 4, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <b>Note:</b> Donees must verify that the odometer reading is
                  recorded in miles, not kilometers. If the odometer is in
                  kilometers, convert it to miles using the formula:
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    Kilometers × 0.62137 = Miles
                  </span>
                  .
                </Alert>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 3: Vehicle or Other Identification Number
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f1098-c-identification-img.webp" />
                  <source srcSet="/assets/images/f1098-c-identification-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f1098-c-identification-img.png"
                    loading="lazy"
                    alt="Box 3: Vehicle or Other Identification Number "
                    width={338}
                    height={82}
                    className="img-responsive"
                    sx={{
                      border: '1px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the Vehicle Identification Number (VIN) for a motor
                  vehicle, the Hull Identification Number for a boat, or the
                  Aircraft Identification Number for an airplane.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 4a: Vehicle sold in arm&apos;s length transaction to
                  unrelated party
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f1098-c-unrelated-img.webp" />
                  <source srcSet="/assets/images/f1098-c-unrelated-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f1098-c-unrelated-img.png"
                    loading="lazy"
                    alt="Box 4a: Vehicle sold in arm's length transaction to unrelated party"
                    width={338}
                    height={42}
                    className="img-responsive"
                    sx={{
                      border: '1px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  The charity must check this box to certify that the donated
                  vehicle was sold for more than $500 in an arm’s length
                  transaction to an unrelated party and fill out boxes 4b and
                  4c.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Skip this box if the vehicle’s claimed value is $500 or less.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 4b: Date of Sale
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f1098-c-date-img.webp" />
                  <source srcSet="/assets/images/f1098-c-date-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f1098-c-date-img.png"
                    loading="lazy"
                    alt="Box 4b: Date of Sale"
                    width={338}
                    height={62}
                    className="img-responsive"
                    sx={{
                      border: '1px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  If you checked box 4a, enter the date the vehicle was sold in
                  the arm&apos;s length transaction. Skip this box if the
                  vehicle&apos;s claimed value is $500 or less.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 4c: Gross proceeds
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f1098-c-gross-img.webp" />
                  <source srcSet="/assets/images/f1098-c-gross-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f1098-c-gross-img.png"
                    loading="lazy"
                    alt="Box 4c: Gross proceeds"
                    width={338}
                    height={42}
                    className="img-responsive"
                    sx={{
                      border: '1px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the gross proceeds the charity received from the sale of
                  the donated vehicle.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If box 4a is checked, you can generally claim a deduction
                  equal to the smaller amount in box 4c or the vehicle&apos;s
                  fair market value (FMV) on the date of the donation.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Skip this box if the vehicle’s claimed value is $500 or less.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 5a: Vehicle will not be transferred before completion of
                  material improvements or significant intervening use
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f1098-c-vehicles-img.webp" />
                  <source srcSet="/assets/images/f1098-c-vehicles-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f1098-c-vehicles-img.png"
                    loading="lazy"
                    alt="Box 5a: Vehicle will not be transferred before completion"
                    width={746}
                    height={62}
                    className="img-responsive"
                    sx={{
                      border: '1px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  The charity must check this box to certify that the donated
                  vehicle will not be sold before significant use or
                  improvement. If checked, you can generally deduct the
                  vehicle’s fair market value (FMV) on the contribution date.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Skip this box if the qualified vehicle has a claimed value of
                  $500 or less.{' '}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 5b: Vehicle to be transferred to a needy individual for
                  significantly below FMV
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f1098-c-transferred-img.webp" />
                  <source srcSet="/assets/images/f1098-c-transferred-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f1098-c-transferred-img.png"
                    loading="lazy"
                    alt="Box 5b: Vehicle to be transferred to a needy"
                    width={746}
                    height={62}
                    className="img-responsive"
                    sx={{
                      border: '1px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  The charity must check this box to certify that the donated
                  vehicle will be transferred to a needy individual as part of
                  its charitable mission to assist the poor and underprivileged
                  in need of transportation.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Skip this box if the vehicle has a claimed value of $500 or
                  less.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 5c: Description of material improvements or significant
                  intervening use and duration of use
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f1098-c-material-img.webp" />
                  <source srcSet="/assets/images/f1098-c-material-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f1098-c-material-img.png"
                    loading="lazy"
                    alt="Box 5c: Description of material improvements"
                    width={746}
                    height={122}
                    className="img-responsive"
                    sx={{
                      border: '1px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Give a detailed description of the planned material
                  improvements or significant intervening use of the vehicle,
                  including the duration of use.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Skip this box if the vehicle&apos;s claimed value is $500 or
                  less.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 6a: Checkbox for whether donee provided goods and services
                  in exchange for the vehicle described.
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f1098-c-donee-img.webp" />
                  <source srcSet="/assets/images/f1098-c-donee-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f1098-c-donee-img.png"
                    loading="lazy"
                    alt="Box 6a: Checkbox for whether donee provided goods"
                    width={746}
                    height={42}
                    className="img-responsive"
                    sx={{
                      border: '1px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Check the box to confirm if any goods or services were
                  provided to the donor in exchange for the vehicle listed in
                  boxes 2a, 2b, 2c, 2d, and 3.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 6b: Value of goods and services provided in exchange for
                  the vehicle described
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f1098-c-services-img.webp" />
                  <source srcSet="/assets/images/f1098-c-services-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f1098-c-services-img.png"
                    loading="lazy"
                    alt="Box 6b: Value of goods and services provided in exchange for the vehicle described"
                    width={746}
                    height={42}
                    className="img-responsive"
                    sx={{
                      border: '1px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  If you checked &quot;Yes&quot; in box 6a, complete box 6b by
                  providing a good faith estimate of the value of goods and
                  services given, including intangible religious benefits.
                  Include the value of any goods and services provided in a
                  different year than the vehicle donation. Refer to Pub. 561
                  for guidance on valuation.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 6c: Description of the goods and services
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f1098-c-good-img.webp" />
                  <source srcSet="/assets/images/f1098-c-good-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f1098-c-good-img.png"
                    loading="lazy"
                    alt="Box 6c: Description of the goods and services"
                    width={746}
                    height={122}
                    className="img-responsive"
                    sx={{
                      border: '1px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  If you checked &quot;Yes&quot; in box 6a, provide a detailed
                  description of the goods and services given to the donor,
                  including any intangible religious benefits. If only
                  intangible religious benefits were received, check the box.
                  These benefits are provided by religious organizations and are
                  not typically sold in commercial transactions.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 7: Checkbox for a vehicle with a claimed value of $500 or
                  less
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f1098-c-claimed-img.webp" />
                  <source srcSet="/assets/images/f1098-c-claimed-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f1098-c-claimed-img.png"
                    loading="lazy"
                    alt="Box 7: Checkbox for a vehicle with a claimed value of $500 or less "
                    width={746}
                    height={42}
                    className="img-responsive"
                    sx={{
                      border: '1px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Check box 7 if the vehicle&apos;s claimed value is $500 or
                  less or if the donor did not provide a TIN. If this box is
                  checked, do not file Copy A with the IRS or provide Copy B to
                  the donor.
                </Typography>
                <Alert severity="info" icon={false} sx={{ borderRadius: 2 }}>
                  <Accordion
                    // defaultExpanded
                    sx={{
                      '&.MuiAccordion-root::before, &.MuiAccordion-root.Mui-expanded::before':
                        {
                          background: 'none !important',
                        },
                      '&.MuiAccordion-root': { background: 'none !important' },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls=""
                      id=""
                      sx={{
                        px: 0,
                        m: 0,
                        justifyContent: 'flex-start',
                        '& .MuiAccordionSummary-content, & .MuiAccordionSummary-content.Mui-expanded':
                          { m: '0 !important', flexGrow: 0 },
                      }}
                    >
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 600,
                          color: '#014361',
                          fontSize: 16,
                        }}
                      >
                        Sources and References
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 0 }}>
                      <Typography
                        component="div"
                        variant="body2"
                        sx={{ mt: 1 }}
                      >
                        The information provided in these resources is based on
                        the IRS guidelines. For more details, please refer the{' '}
                        <Link
                          href="https://www.irs.gov/pub/irs-pdf/i1098c.pdf"
                          sx={{ color: '#0202ff' }}
                          target="_blank"
                        >
                          instructions of 1098-C
                        </Link>{' '}
                        and{' '}
                        <Link
                          href="https://www.irs.gov/instructions/i1099gi"
                          sx={{ color: '#0202ff' }}
                          target="_blank"
                        >
                          General instructions of Information returns
                        </Link>
                        .
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                </Alert>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>

      <Container maxWidth="lg" sx={{ mb: 5, my: { xs: 6, sm: 6, md: 6 } }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={{ xs: 2, md: 5 }}
        >
          <Box
            sx={{
              width: '100%',
              textAlign: 'center',
            }}
          >
            <Typography
              variant="h2"
              sx={{
                fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                fontWeight: 600,
                mb: 2,
                textAlign: 'center',
                lineHeight: 1.5,
              }}
            >
              File form 1098-C with TaxZerone
            </Typography>
            <Typography
              sx={{
                fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                fontWeight: 500,
                color: '#333333',
                mb: 2,
              }}
            >
              Say goodbye to paperwork! TaxZerone helps you file your
              submissions on time to avoid penalties!
            </Typography>

            <Box sx={{ textAlign: { xs: 'center', md: 'center' } }}>
              <Button
                variant="contained"
                sx={{
                  mt: 1,
                  transition: 'all 0.3s ease 0s',
                  '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
                }}
                className="btn-primary"
                href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=1098Cinstructions`}
              >
                Start E-file Now!
              </Button>
            </Box>
          </Box>
        </Stack>
      </Container>
    </AppLayout>
  );
};

export default Form1098CInstructions;
